/**
 * @file Fail-closed reCAPTCHA v3 guard for form submission and authentication flows.
 *
 * Wraps `useReCaptcha()` so a missing plugin (CDN blocked, ad-blocker, network error)
 * or a verification rejection is treated as a hard failure — the wrapped `work` callback
 * never runs. This is the security boundary for credential-stuffing and contact-form
 * spam defence; an `else` branch that silently runs `work` would invert the intent of
 * adding reCAPTCHA in the first place.
 *
 * Exposes a reactive `isVerifying` flag for binding to button disabled state, and a
 * `withRecaptcha(action, work)` helper that returns `true` only when verification
 * succeeded and `work` was attempted.
 */

import { ref } from 'vue'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

export function useRecaptchaGuard() {
  const recaptcha = useReCaptcha()
  const toast = useToast()
  const { t } = useI18n()
  const isVerifying = ref(false)

  async function withRecaptcha(
    action: string,
    work: (token: string) => Promise<unknown> | unknown
  ): Promise<boolean> {
    if (isVerifying.value) return false
    isVerifying.value = true
    try {
      if (!recaptcha) {
        toast.error(t('errors.recaptcha-failed'))
        return false
      }
      await recaptcha.recaptchaLoaded()
      const token = await recaptcha.executeRecaptcha(action)
      await work(token)
      return true
    } catch (error) {
      console.error('reCAPTCHA error:', error)
      toast.error(t('errors.recaptcha-failed'))
      return false
    } finally {
      isVerifying.value = false
    }
  }

  return { isVerifying, withRecaptcha }
}
